import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { ApplicationTab } from '@/components/submission-detail/ApplicationTab';
import { DocumentsTab } from '@/components/submission-detail/DocumentsTab';
import { NotesTab } from '@/components/submission-detail/NotesTab';
import { OverviewTab } from '@/components/submission-detail/OverviewTab';
import { VideoTab } from '@/components/submission-detail/VideoTab';
import { WarningsBanner } from '@/components/submission-detail/WarningsBanner';
import { StatusBadge } from '@/components/submissions/StatusBadge';
import { computeSubmissionWarnings } from '@/lib/submissionWarnings';
import { cn } from '@/lib/utils';
import { ApiError } from '@/services/apiClient';
import { getDocumentsForApplication } from '@/services/documentService';
import { getSubmissionDetail } from '@/services/submissionService';

import type { HrDocument } from '@/types/document.types';
import type { SubmissionDetail } from '@/types/submission.types';

type Tab = 'overview' | 'application' | 'documents' | 'video' | 'notes';

const TABS: Tab[] = ['overview', 'application', 'documents', 'video', 'notes'];

export function SubmissionDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [documents, setDocuments] = useState<HrDocument[]>([]);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);

  const handleDocumentsLoaded = useCallback((docs: HrDocument[]) => {
    setDocuments(docs);
  }, []);

  const refreshDocuments = useCallback(async () => {
    if (!detail) return;
    try {
      const docs = await getDocumentsForApplication(detail.id);
      setDocuments(docs);
    } catch {
      // silently fail
    }
  }, [detail]);

  const handleDecisionMade = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getSubmissionDetail(id);
      setDetail(data);
    } catch {
      // silently fail
    }
  }, [id]);

  const warnings = useMemo(() => {
    if (!detail) return [];
    return computeSubmissionWarnings(detail, documents, t);
  }, [detail, documents, t]);

  useEffect(() => {
    if (!id) return undefined;
    let isCancelled = false;

    setIsLoading(true);
    setError(null);

    getSubmissionDetail(id)
      .then((result) => {
        if (!isCancelled) {
          setDetail(result);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!isCancelled) {
          if (err instanceof ApiError && err.statusCode === 404) {
            setDetail(null);
          } else {
            setError(err instanceof Error ? err.message : t('submissionDetail.errorLoading'));
          }
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [id, t]);

  useEffect(() => {
    if (!detail) return;
    void getDocumentsForApplication(detail.id).then((docs) => {
      setDocuments(docs);
    });
  }, [detail]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-4'>
        <button
          type='button'
          onClick={() => void navigate('/dashboard')}
          className='flex items-center text-sm text-gray-600 hover:text-gray-900'>
          <ArrowLeft className='h-4 w-4 mr-1' />
          {t('submissionDetail.backToSubmissions')}
        </button>
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{error}</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className='space-y-4'>
        <button
          type='button'
          onClick={() => void navigate('/dashboard')}
          className='flex items-center text-sm text-gray-600 hover:text-gray-900'>
          <ArrowLeft className='h-4 w-4 mr-1' />
          {t('submissionDetail.backToSubmissions')}
        </button>
        <p className='text-gray-500'>{t('submissionDetail.notFound')}</p>
      </div>
    );
  }

  const fullName = `${detail.firstName} ${detail.lastName}`;

  return (
    <div className='space-y-6'>
      {/* Back button */}
      <button
        type='button'
        onClick={() => void navigate('/dashboard')}
        className='flex items-center text-sm text-gray-600 hover:text-gray-900'>
        <ArrowLeft className='h-4 w-4 mr-1' />
        {t('submissionDetail.backToSubmissions')}
      </button>

      {/* Header */}
      <div className='flex items-center gap-4'>
        <h1 className='text-2xl font-bold text-gray-900'>{fullName}</h1>
        <span className='text-sm font-mono text-gray-500'>{detail.applicationId}</span>
        <StatusBadge stage={detail.stage} />
      </div>

      {/* Tab bar */}
      <div className='border-b border-gray-200'>
        <nav className='flex -mb-px space-x-8'>
          {TABS.map((tab) => (
            <button
              key={tab}
              type='button'
              onClick={() => setActiveTab(tab)}
              className={cn(
                'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              )}>
              {t(`submissionDetail.tabs.${tab}`)}
            </button>
          ))}
        </nav>
      </div>

      {warnings.length > 0 ? <WarningsBanner warnings={warnings} onPreviewDocument={setPreviewDocumentId} /> : null}

      {/* Tab content */}
      {activeTab === 'overview' ? (
        <OverviewTab detail={detail} onDecisionMade={() => void handleDecisionMade()} />
      ) : null}
      {activeTab === 'application' ? <ApplicationTab detail={detail} warnings={warnings} /> : null}
      {activeTab === 'documents' ? (
        <DocumentsTab applicationId={detail.id} onDocumentsLoaded={handleDocumentsLoaded} applicationData={detail} />
      ) : null}
      {activeTab === 'video' ? <VideoTab video={detail.video} applicationId={detail.id} /> : null}
      {activeTab === 'notes' ? <NotesTab applicationId={detail.id} /> : null}

      {previewDocumentId ? (
        <DocumentPreviewModal
          documentId={previewDocumentId}
          isOpen
          onClose={() => setPreviewDocumentId(null)}
          onStatusChange={() => void refreshDocuments()}
          applicationData={detail}
        />
      ) : null}
    </div>
  );
}
